import type React from 'react';
import {
  LayoutDashboard,
  Users,
  Layers,
  MessageSquareText,
  Wrench,
  FileText,
  Download,
} from 'lucide-react';

/**
 * Admin navigation structure (AR-285).
 *
 * The Admin area uses the same two-column pattern as the rest of the app:
 * the primary sidebar lists sections, the secondary sidebar lists the pages
 * of the selected section. Adding a section or a page here is all that is
 * needed to extend the navigation — no component changes.
 */

export interface AdminNavPage {
  label: string;
  path: string;
  /** Rendered but not navigable (shows a "Soon" badge). */
  disabled?: boolean;
  comingSoon?: boolean;
  /** Only visible to SYSTEM_ADMIN (hidden from basic ADMIN). */
  systemAdminOnly?: boolean;
  /** Requires SYSTEM_ADMIN *and* the agenticAdminAdvanced flag. */
  advancedOnly?: boolean;
  /** Requires Finances access — the resolved invoicingMvp flag (AR-251). */
  financesGated?: boolean;
  /**
   * RBAC admin-module key governing this page. A per-user grant (read/full)
   * on this key makes the page visible to Admins the `systemAdminOnly` flag
   * would otherwise hide (Role & Access panel assignable modules).
   */
  moduleKey?: string;
  /**
   * Minimum grant level required (default 'read'). Write-capable pages set
   * 'full' so a read grant only unlocks the module's view-only pages.
   */
  minLevel?: 'read' | 'full';
  /** Requires the resolved `onboarding` parent flag (AR-171). */
  onboardingGated?: boolean;
}

export interface AdminNavSection {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Pages of the section; the first visible one is the section default. */
  pages: AdminNavPage[];
}

export const ADMIN_NAV_SECTIONS: AdminNavSection[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard,
    pages: [{ label: 'Overview', path: '/admin', systemAdminOnly: true }],
  },
  {
    id: 'user-management',
    label: 'User Management',
    icon: Users,
    pages: [{ label: 'User Record Table', path: '/admin/users' }],
  },
  // Member Activation is a sibling of User Management, not nested inside it
  // (AR-328). Access is RBAC-controlled via the `admin.member-activation`
  // module, so it stays visible to basic ADMIN — but the whole section rides
  // behind the `onboarding` parent flag until onboarding ships (AR-171).
  {
    id: 'member-activation',
    label: 'Member Activation',
    icon: Layers,
    pages: [
      { label: 'Modules', path: '/admin/activation/modules', onboardingGated: true },
      { label: 'Archive', path: '/admin/activation/archive', onboardingGated: true },
      { label: 'KPIs', path: '/admin/activation/kpis', onboardingGated: true },
    ],
  },
  {
    id: 'content-management',
    label: 'Content Management',
    icon: MessageSquareText,
    // AR-379: Content Sync (posts mirrored from PRO) is the section's landing page.
    pages: [
      { label: 'Content Sync', path: '/admin/content' },
      { label: 'Prompt Manager', path: '/admin/prompts' },
    ],
  },
  {
    id: 'developer-controls',
    label: 'Developer Controls',
    icon: Wrench,
    pages: [
      { label: 'Product Updates', path: '/admin/product-updates', systemAdminOnly: true, moduleKey: 'admin.developer-controls', minLevel: 'full' },
      { label: 'Feature Gates', path: '/admin/feature-gates', systemAdminOnly: true, moduleKey: 'admin.developer-controls', minLevel: 'full' },
      { label: 'Cost Dashboard', path: '/admin/cost-dashboard', systemAdminOnly: true, moduleKey: 'admin.developer-controls' },
      { label: 'MCP Tools', path: '/admin/mcp-tools', systemAdminOnly: true, moduleKey: 'admin.developer-controls', minLevel: 'full' },
      { label: 'Skills Management', path: '/admin/skills', systemAdminOnly: true, moduleKey: 'admin.developer-controls', minLevel: 'full' },
      {
        label: 'Agentic Rollout',
        path: '/admin/orchestration',
        systemAdminOnly: true,
        advancedOnly: true,
        moduleKey: 'admin.developer-controls',
        minLevel: 'full',
      },
      {
        label: 'Settings',
        path: '/admin/settings',
        systemAdminOnly: true,
        disabled: true,
        comingSoon: true,
      },
    ],
  },
  {
    id: 'audit-logs',
    label: 'Audit Logs',
    icon: FileText,
    pages: [
      { label: 'Finances Sync Error Log', path: '/admin/sync-error-log', financesGated: true },
      { label: 'Admin Audit Log', path: '/admin/audit', systemAdminOnly: true, moduleKey: 'admin.audit-logs' },
    ],
  },
  {
    id: 'exports',
    label: 'Exports',
    icon: Download,
    pages: [{ label: 'Threads Export', path: '/admin/chat-export', systemAdminOnly: true, moduleKey: 'admin.exports' }],
  },
];

export interface AdminNavAccess {
  isSystemAdmin: boolean;
  advancedFlagEnabled: boolean;
  /** Resolved invoicingMvp flag — gates the Finances Sync Error Log (AR-251). */
  financesEnabled: boolean;
  /**
   * Current user's effective admin-module access (Role & Access panel grants).
   * A read/full level on a page's `moduleKey` overrides `systemAdminOnly`
   * hiding for Admins. Optional — omitted means "no grants" (legacy behavior).
   */
  moduleAccess?: Record<string, 'none' | 'read' | 'full'>;
  /** Resolved `onboarding` flag — gates the Member Activation section (AR-171). */
  onboardingEnabled: boolean;
}

/** Pages of a section the current user may see. */
export const visibleAdminPages = (
  section: AdminNavSection,
  {
    isSystemAdmin,
    advancedFlagEnabled,
    financesEnabled,
    onboardingEnabled,
    moduleAccess,
  }: AdminNavAccess,
): AdminNavPage[] =>
  section.pages.filter((page) => {
    // Flag gates come first: a page behind an off flag is hidden from everyone,
    // System Admin included, so the rollout switch means the same for all roles.
    if (page.onboardingGated && !onboardingEnabled) return false;
    // A Role & Access grant (read/full) on the page's module unlocks a
    // systemAdminOnly page for an Admin; write-capable pages need 'full'.
    const grantLevel = page.moduleKey ? moduleAccess?.[page.moduleKey] : undefined;
    const grantMeetsMin =
      grantLevel === 'full' || (grantLevel === 'read' && (page.minLevel ?? 'read') === 'read');
    const granted = isSystemAdmin || grantMeetsMin;
    if (page.advancedOnly) return granted && advancedFlagEnabled;
    if (page.systemAdminOnly && !granted) return false;
    if (page.financesGated) return financesEnabled;
    return true;
  });

/** Sections with at least one visible page, each narrowed to its visible pages. */
export const visibleAdminSections = (access: AdminNavAccess): AdminNavSection[] =>
  ADMIN_NAV_SECTIONS.map((section) => ({
    ...section,
    pages: visibleAdminPages(section, access),
  })).filter((section) => section.pages.length > 0);

/** Where a section's primary-nav entry navigates to (its default sub-page). */
export const adminSectionDefaultPath = (section: AdminNavSection): string | undefined =>
  section.pages.find((page) => !page.disabled)?.path;

const isPathActive = (location: string, path: string): boolean =>
  location === path || location.startsWith(path + '/');

/**
 * Section that owns the current location. Sub-paths (e.g. `/admin/users/42`)
 * keep their section lit. Matches the longest page path so that `/admin`
 * (Overview) never shadows the other sections.
 */
export const activeAdminSection = (
  location: string,
  sections: AdminNavSection[] = ADMIN_NAV_SECTIONS,
): AdminNavSection | undefined => {
  let match: { section: AdminNavSection; length: number } | undefined;

  for (const section of sections) {
    for (const page of section.pages) {
      if (isPathActive(location, page.path) && (!match || page.path.length > match.length)) {
        match = { section, length: page.path.length };
      }
    }
  }

  return match?.section;
};

/** Whether a sub-nav page is the one currently open. */
export const isAdminPageActive = (location: string, page: AdminNavPage): boolean =>
  !page.disabled && isPathActive(location, page.path);
